import React, { useState, useEffect } from 'react';
import { File } from '../../services/fileService';
import { FileType } from '../../services/fileTypeService';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';

interface EditFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: File | null;
  onSave: (id: number, description: string, fileTypeId?: number) => Promise<void>;
  isSaving?: boolean;
  fileTypes?: FileType[];
}

export const EditFileModal: React.FC<EditFileModalProps> = ({
  isOpen,
  onClose,
  file,
  onSave,
  isSaving = false,
  fileTypes = [],
}) => {
  const [description, setDescription] = useState('');
  const [fileTypeId, setFileTypeId] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  // Update local state when file changes
  useEffect(() => {
    if (file) {
      setDescription(file.description || '');
      setFileTypeId(file.file_type_id);
    }
  }, [file]);

  const handleSave = async () => {
    try {
      setError(null);
      if (file) {
        await onSave(file.id, description, fileTypeId);
      }
      // onClose will be called by parent after successful save
    } catch (err: any) {
      setError(err.message || 'Failed to save file');
    }
  };

  const handleCancel = () => {
    setDescription(file?.description || '');
    setFileTypeId(file?.file_type_id);
    setError(null);
    onClose();
  };

  if (!isOpen || !file) return null;

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleCancel}
      title="Edit file"
      isLoading={isSaving}
    >
      {/* Description Input */}
      <div className="mb-4">
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={POPUP_STYLES.input}
          placeholder="Enter file description..."
          disabled={isSaving}
        />
      </div>

      {/* File Type Dropdown */}
      {fileTypes.length > 0 && (
        <div className="mb-4">
          <select
            value={fileTypeId || ''}
            onChange={(e) => setFileTypeId(e.target.value ? parseInt(e.target.value) : undefined)}
            className={POPUP_STYLES.input}
            disabled={isSaving}
          >
            <option value="">No File Type</option>
            {fileTypes.map((ft) => (
              <option key={ft.id} value={ft.id}>
                {ft.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-center gap-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={POPUP_STYLES.button}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className={POPUP_STYLES.button}
        >
          Cancel
        </button>
      </div>
    </ModalWrapper>
  );
};
